import { httpsCallable, functions } from '@/lib/functions';
import { collection, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { auth } from '@/lib/firebase';
import { ensureFreshToken, withAuthRetry } from '@/services/authSessionRecovery';

export type DocumentType = 'passport' | 'visa' | 'driving_license' | 'health_record' | 'insurance' | 'other';

export type EntryType = 'manual' | 'upload';

// Document-specific metadata types
export interface PassportMetadata {
  number: string;
  issuingCountry: string;
  issueDate?: Date | null;
  expirationDate: Date;
  nationality?: string;
  placeOfBirth?: string;
  fullName?: string;
}

export interface DrivingLicenseMetadata {
  number: string;
  issuingState?: string;
  issuingCountry: string;
  issueDate?: Date | null;
  expirationDate: Date;
  licenseClass?: string;
  address?: string;
}

export interface VisaMetadata {
  number: string;
  issuingCountry: string;
  issueDate?: Date | null;
  expirationDate: Date;
  visaType?: string;
  entryDate?: Date | null;
  exitDate?: Date | null;
  numberOfEntries?: string;
}

export interface HealthRecordMetadata {
  recordType: string;
  providerName: string;
  date: Date;
  doctorName?: string;
  medicalRecordNumber?: string;
  diagnosis?: string;
  notes?: string;
}

export interface InsuranceMetadata {
  policyNumber: string;
  provider: string;
  coverageStartDate: Date;
  coverageEndDate?: Date | null;
  policyType?: string;
  coverageDetails?: string;
  contactInfo?: string;
}

export interface OtherDocumentMetadata {
  documentNumber: string;
  issuingAuthority: string;
  issueDate?: Date | null;
  expirationDate?: Date | null;
  notes?: string;
}

export type DocumentMetadata =
  | PassportMetadata
  | DrivingLicenseMetadata
  | VisaMetadata
  | HealthRecordMetadata
  | InsuranceMetadata
  | OtherDocumentMetadata;

export type Document = {
  id: string;
  name: string;
  type: DocumentType;
  entryType: EntryType;
  /**
   * Optional list of trip IDs this document is associated with.
   * Used for auto-download and trip-scoped document views.
   */
  tripIds?: string[];
  storagePath?: string | null;
  encryptedKey?: string;
  encryptionMethod?: string;
  expiresOn?: Date | null;
  size?: number | null;
  contentType?: string | null;
  metadata?: DocumentMetadata;
  createdAt: Date;
  createdBy: string;
  status: string;
  category?: string;
  parseStatus?: string;
  updatedAt?: Date;
};

type FirestoreDocument = {
  name: string;
  type: string;
  entryType?: string;
  tripIds?: string[] | null;
  storagePath?: string | null;
  encryptedKey?: string;
  encryptionMethod?: string;
  expiresOn?: Timestamp | string | null;
  size?: number | null;
  contentType?: string | null;
  metadata?: Record<string, unknown>;
  createdAt: Timestamp | Date;
  createdBy: string;
  status: string;
};

// Convert Firestore document to app Document type
const convertFirestoreDocument = (id: string, data: FirestoreDocument): Document => {
  const convertDate = (date: Timestamp | string | Date | undefined): Date | undefined => {
    if (!date) return undefined;
    if (date instanceof Timestamp) return date.toDate();
    if (date instanceof Date) return date;
    if (typeof date === 'string') return new Date(date);
    return undefined;
  };

  const convertMetadataDates = (metadata: Record<string, unknown> | undefined): DocumentMetadata | undefined => {
    if (!metadata) return undefined;
    
    const converted = { ...metadata };
    
    // Convert date strings in metadata to Date objects
    const dateFields = ['issueDate', 'expirationDate', 'entryDate', 'exitDate', 'date', 'coverageStartDate', 'coverageEndDate'];
    for (const field of dateFields) {
      if (converted[field] && typeof converted[field] === 'string') {
        converted[field] = new Date(converted[field] as string);
      } else if (converted[field] instanceof Timestamp) {
        converted[field] = (converted[field] as Timestamp).toDate();
      }
    }
    
    return converted as unknown as DocumentMetadata;
  };

  return {
    id,
    name: data.name,
    type: (data.type || 'other') as DocumentType,
    entryType: (data.entryType || 'upload') as EntryType,
    tripIds: Array.isArray(data.tripIds) ? data.tripIds.filter((t) => typeof t === 'string' && t.length > 0) : undefined,
    storagePath: data.storagePath || null,
    encryptedKey: data.encryptedKey,
    encryptionMethod: data.encryptionMethod,
    expiresOn: convertDate(data.expiresOn ?? undefined) || null,
    size: data.size || null,
    contentType: data.contentType || null,
    metadata: convertMetadataDates(data.metadata),
    createdAt: convertDate(data.createdAt) || new Date(),
    createdBy: data.createdBy,
    status: data.status || 'stored',
  };
};

// Prepare upload request
type PrepareUploadRequest = {
  action: 'prepareUpload';
  fileName: string;
  contentType: string;
};

type PrepareUploadResponse = {
  uploadUrl: string;
  storagePath: string;
  expiresAt: string;
};

// Finalize upload request
type FinalizeUploadRequest = {
  action: 'finalize';
  document: {
    name: string;
    type: string;
    entryType: string;
    storagePath?: string | null;
    size?: number | null;
    contentType?: string | null;
    encryptedKey?: string;
    encryptionMethod?: string;
    expiresOn?: string;
    metadata?: Record<string, unknown>;
    parseStatus?: string;
    ocrRawText?: string | null;
    fieldConfidence?: Record<string, number>;
    parseWarnings?: string[];
    missingRequiredFields?: string[];
    detectedDocumentType?: string | null;
    ocrEngine?: string | null;
    extractionSource?: string | null;
  };
};

type FinalizeUploadResponse = {
  documentId: string;
};

// Get document request
type GetDocumentRequest = {
  action: 'get';
  documentId: string;
};

type GetDocumentResponse = {
  document: Record<string, unknown>;
  downloadUrl: string;
  expiresAt: string;
};

// Delete document request
type DeleteDocumentRequest = {
  action: 'delete';
  documentId: string;
};

type DeleteDocumentResponse = {
  deleted: true;
};

// Update document request
type UpdateDocumentRequest = {
  action: 'update';
  documentId: string;
  document: {
    name: string;
    type: DocumentType;
    expiresOn?: string | null;
    metadata?: Record<string, unknown> | null;
  };
};

type UpdateDocumentResponse = {
  updated: true;
};

// List documents request
type ListDocumentsRequest = {
  action: 'list';
};

type ListDocumentsResponse = {
  documents: Array<Record<string, unknown>>;
};

const callDocumentVault = httpsCallable<
  | PrepareUploadRequest
  | FinalizeUploadRequest
  | GetDocumentRequest
  | DeleteDocumentRequest
  | UpdateDocumentRequest
  | ListDocumentsRequest,
  | PrepareUploadResponse
  | FinalizeUploadResponse
  | GetDocumentResponse
  | DeleteDocumentResponse
  | UpdateDocumentResponse
  | ListDocumentsResponse
>(functions, 'documentVault');

async function ensureAuthenticatedForCallable(): Promise<void> {
  // Ensure Auth finishes hydrating (especially after cold start)
  // and that we have a current user so callable requests include auth context.
  try {
    await (auth as any)?.authStateReady?.();
  } catch {
    // ignore: older SDKs might not support authStateReady
  }

  if (!auth.currentUser) {
    throw new Error('Please sign in again to save documents.');
  }

  // Force a token refresh in case the cached token is stale.
  // This also validates that the client can access auth state properly.
  await ensureFreshToken(true);

  if (__DEV__) {
    const authProjectId = (auth as any)?.app?.options?.projectId;
    const functionsProjectId = (functions as any)?.app?.options?.projectId;
    const functionsRegion = (functions as any)?._region || (functions as any)?.region || 'unknown';
    const functionsEmulatorOrigin =
      (functions as any)?._emulatorOrigin ||
      (functions as any)?.emulatorOrigin ||
      (functions as any)?._customDomain ||
      null;

    console.warn('[DocumentVault] auth context debug', {
      authState: auth.currentUser ? 'ready' : 'missing',
      authProjectId: authProjectId ?? null,
      functionsProjectId: functionsProjectId ?? null,
      functionsRegion,
      functionsEmulatorOrigin,
    });
  }
}

async function debugRawCallableOnce(data: any): Promise<void> {
  if (!__DEV__) return;
  try {
    const uid = auth.currentUser?.uid;
    if (!uid || !auth.currentUser) return;

    // For v1+v2 callable compatibility, Firebase SDK uses the cloudfunctions.net hostname.
    const projectId = (auth as any)?.app?.options?.projectId;
    const region = (functions as any)?._region || (functions as any)?.region || 'us-central1';
    if (!projectId) return;

    const url = `https://${region}-${projectId}.cloudfunctions.net/documentVault`;
    const token = await auth.currentUser.getIdToken();

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ data }),
    });

    await res.text();
    console.warn('[DocumentVault] raw callable probe', {
      status: res.status,
    });
  } catch (e: any) {
    const message = e instanceof Error ? e.message : String(e);
    console.warn('[DocumentVault] raw callable probe failed', { message });
  }
}

function safeDecodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    // Pad base64 string if needed
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = globalThis.atob ? globalThis.atob(padded) : Buffer.from(padded, 'base64').toString('utf8');
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Subscribe to user documents in real-time
 */
export function subscribeToUserDocuments(
  userId: string,
  callback: (documents: Document[]) => void
): () => void {
  const documentsRef = collection(db, 'users', userId, 'documents');
  
  let unsubscribeFn: (() => void) | null = null;
  let isCleanedUp = false;
  
  try {
    unsubscribeFn = onSnapshot(
      documentsRef,
      (snapshot) => {
        // Don't process updates if already cleaned up
        if (isCleanedUp) {
          return;
        }
        
        // Defer heavy work to avoid blocking message handlers
        setTimeout(() => {
          if (isCleanedUp) {
            return;
          }
          try {
            const documents = snapshot.docs.map((doc) =>
              convertFirestoreDocument(doc.id, doc.data() as FirestoreDocument)
            );
            // Sort by creation date, newest first
            documents.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
            callback(documents);
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('[DocumentVault] Error processing snapshot', { message });
            if (!isCleanedUp) {
              try {
                callback([]);
              } catch (callbackError) {
                const callbackMessage =
                  callbackError instanceof Error ? callbackError.message : String(callbackError);
                console.error('[DocumentVault] Error in callback', { message: callbackMessage });
              }
            }
          }
        }, 0);
      },
      (error) => {
        // Don't process errors if already cleaned up
        if (isCleanedUp) {
          return;
        }
        
        const message = error instanceof Error ? error.message : String(error);
        console.error('[DocumentVault] Error subscribing to documents', { message });
        // Always call callback with empty array to ensure loading state is cleared
        // This prevents the UI from being stuck in loading state
        try {
          callback([]);
        } catch (callbackError) {
          const callbackMessage =
            callbackError instanceof Error ? callbackError.message : String(callbackError);
          console.error('[DocumentVault] Error in callback', { message: callbackMessage });
        }
      }
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[DocumentVault] Error setting up subscription', { message });
    // Call callback with empty array to ensure loading state is cleared
    try {
      callback([]);
    } catch (callbackError) {
      const callbackMessage = callbackError instanceof Error ? callbackError.message : String(callbackError);
      console.error('[DocumentVault] Error in callback after setup failure', { message: callbackMessage });
    }
    // Return a no-op cleanup function if setup fails
    return () => {};
  }

  return () => {
    if (isCleanedUp) {
      return; // Already cleaned up, prevent double cleanup
    }
    isCleanedUp = true;
    
    if (unsubscribeFn) {
      try {
        unsubscribeFn();
      } catch (error) {
        // Silently ignore cleanup errors - they're often due to Firestore internal state
      } finally {
        unsubscribeFn = null; // Clear reference after cleanup
      }
    }
  };
}

/**
 * Prepare document upload - gets signed URL for upload
 */
export async function prepareDocumentUpload(
  fileName: string,
  contentType: string
): Promise<{ uploadUrl: string; storagePath: string; expiresAt: Date }> {
  try {
    await ensureAuthenticatedForCallable();
    const result = await withAuthRetry(() =>
      callDocumentVault({
        action: 'prepareUpload',
        fileName,
        contentType,
      })
    );

    const data = result.data as PrepareUploadResponse;
    return {
      uploadUrl: data.uploadUrl,
      storagePath: data.storagePath,
      expiresAt: new Date(data.expiresAt),
    };
  } catch (error: any) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('[DocumentVault] Error preparing document upload', { message: errorMessage });
    if (error?.code === 'functions/unauthenticated' || `${error?.message || ''}`.toLowerCase().includes('unauthenticated')) {
      if (__DEV__) {
        try {
          const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
          const payload = token ? safeDecodeJwtPayload(token) : null;
          const authProjectId = (auth as any)?.app?.options?.projectId;
          const functionsProjectId = (functions as any)?.app?.options?.projectId;
          // Use warn so it shows even if console.log is stripped in native builds.
          console.warn('[DocumentVault] unauthenticated diagnostic', {
            tokenAud: payload?.aud ?? null,
            authProjectId: authProjectId ?? null,
            functionsProjectId: functionsProjectId ?? null,
          });

          // Probe the callable endpoint with a manual fetch + Authorization header.
          // If this succeeds, the SDK isn't attaching auth; if it fails, the backend is rejecting the token.
          await debugRawCallableOnce({
            action: 'prepareUpload',
            fileName,
            contentType,
          });
        } catch (e: any) {
          const message = e instanceof Error ? e.message : String(e);
          console.warn('[DocumentVault] unauthenticated diagnostic failed', { message });
        }
      }
      // NOTE: This can also happen if the app is signed into a different Firebase project
      // than the one hosting the Functions endpoint (token "aud" mismatch).
      throw new Error('Auth failed for document upload. Please re-open the app and try again.');
    }
    throw error;
  }
}

/**
 * Finalize document upload after file has been uploaded to storage, or save manual entry
 */
export async function finalizeDocumentUpload(
  document: {
    name: string;
    type: DocumentType;
    entryType: EntryType;
    storagePath?: string | null;
    size?: number | null;
    contentType?: string | null;
    encryptedKey?: string;
    encryptionMethod?: string;
    expiresOn?: Date;
    metadata?: DocumentMetadata;
    parseStatus?: string;
    ocrRawText?: string | null;
    fieldConfidence?: Record<string, number>;
    parseWarnings?: string[];
    missingRequiredFields?: string[];
    detectedDocumentType?: string | null;
    ocrEngine?: string | null;
    extractionSource?: string | null;
  }
): Promise<string> {
  // Convert metadata dates to ISO strings for Firestore
  const metadataForFirestore = document.metadata ? convertMetadataForFirestore(document.metadata) : undefined;

  // Retry a bit for upload entries (Storage can be slightly eventual, especially on emulator/dev).
  const maxAttempts = document.entryType === 'upload' ? 3 : 1;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await ensureAuthenticatedForCallable();

      const result = await withAuthRetry(() =>
        callDocumentVault({
          action: 'finalize',
          document: {
            name: document.name,
            type: document.type,
            entryType: document.entryType,
            storagePath: document.storagePath || null,
            size: document.size ?? null,
            contentType: document.contentType ?? null,
            encryptedKey: document.encryptedKey,
            encryptionMethod: document.encryptionMethod || 'none',
            expiresOn: document.expiresOn?.toISOString(),
            metadata: metadataForFirestore,
            parseStatus: document.parseStatus,
            ocrRawText: document.ocrRawText ?? undefined,
            fieldConfidence: document.fieldConfidence,
            parseWarnings: document.parseWarnings,
            missingRequiredFields: document.missingRequiredFields,
            detectedDocumentType: document.detectedDocumentType ?? undefined,
            ocrEngine: document.ocrEngine ?? undefined,
            extractionSource: document.extractionSource ?? undefined,
          },
        })
      );

      const data = result.data as FinalizeUploadResponse;
      return data.documentId;
    } catch (error: any) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[DocumentVault] Error finalizing document upload', { message });

      // Retry on not-found for uploads (give Storage a moment)
      if (
        attempt < maxAttempts &&
        document.entryType === 'upload' &&
        (error?.code === 'functions/not-found' || `${error?.message || ''}`.toLowerCase().includes('not found'))
      ) {
        await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
        continue;
      }

      // Provide more helpful error messages
      if (error?.code === 'functions/unauthenticated') {
        throw new Error('Your session expired. Please sign in again to save documents.');
      } else if (error?.code === 'functions/internal') {
        const errorMessage = error?.message || 'An internal server error occurred';
        throw new Error(`Failed to save document: ${errorMessage}. Please try again.`);
      } else if (error?.code === 'functions/not-found') {
        throw new Error('The uploaded file was not found. Please try uploading again.');
      } else if (error?.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to perform this action.');
      } else if (error?.code === 'functions/invalid-argument') {
        throw new Error(error?.message || 'Invalid document data. Please check your input.');
      } else if (error?.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to save document. Please try again.');
      }
    }
  }

  // Should never reach here
  throw new Error('Failed to save document. Please try again.');
}

/**
 * Convert metadata dates to ISO strings for Firestore storage
 */
function convertMetadataForFirestore(metadata: DocumentMetadata): Record<string, unknown> {
  const converted = { ...metadata } as Record<string, unknown>;
  
  const dateFields = ['issueDate', 'expirationDate', 'entryDate', 'exitDate', 'date', 'coverageStartDate', 'coverageEndDate'];
  for (const field of dateFields) {
    if (converted[field] instanceof Date) {
      converted[field] = (converted[field] as Date).toISOString();
    } else if (converted[field] === null || converted[field] === undefined) {
      // Keep null/undefined as is
    }
  }
  
  return converted;
}

/**
 * Get document with download URL
 */
export async function getDocumentWithDownloadUrl(
  documentId: string
): Promise<{ document: Document; downloadUrl: string; expiresAt: Date }> {
  try {
    await ensureAuthenticatedForCallable();
    const result = await withAuthRetry(() =>
      callDocumentVault({
        action: 'get',
        documentId,
      })
    );

    const data = result.data as GetDocumentResponse;
    const doc = convertFirestoreDocument(documentId, data.document as FirestoreDocument);
    
    return {
      document: doc,
      downloadUrl: data.downloadUrl,
      expiresAt: new Date(data.expiresAt),
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[DocumentVault] Error getting document', { message });
    throw error;
  }
}

/**
 * Delete a document
 */
export async function deleteDocument(documentId: string): Promise<void> {
  try {
    await ensureAuthenticatedForCallable();
    await withAuthRetry(() =>
      callDocumentVault({
        action: 'delete',
        documentId,
      })
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[DocumentVault] Error deleting document', { message });
    throw error;
  }
}

/**
 * Update an existing document (name/type/metadata/expiresOn).
 * Does NOT change/replace the uploaded file; it only edits the record.
 */
export async function updateDocument(
  documentId: string,
  update: {
    name: string;
    type: DocumentType;
    expiresOn?: Date | null;
    metadata?: DocumentMetadata | null;
  }
): Promise<void> {
  await ensureAuthenticatedForCallable();

  const metadataForFirestore =
    update.metadata === undefined
      ? undefined
      : update.metadata === null
        ? null
        : convertMetadataForFirestore(update.metadata);

  await withAuthRetry(() =>
    callDocumentVault({
      action: 'update',
      documentId,
      document: {
        name: update.name,
        type: update.type,
        expiresOn: update.expiresOn ? update.expiresOn.toISOString() : null,
        metadata: metadataForFirestore ?? null,
      },
    })
  );
}

/**
 * List all documents for user
 */
export async function listDocuments(): Promise<Document[]> {
  try {
    await ensureAuthenticatedForCallable();
    const result = await withAuthRetry(() =>
      callDocumentVault({
        action: 'list',
      })
    );

    const data = result.data as ListDocumentsResponse;
    return data.documents.map((doc, index) =>
      convertFirestoreDocument((doc.id as string) || `doc-${index}`, doc as FirestoreDocument)
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[DocumentVault] Error listing documents', { message });
    throw error;
  }
}

